import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "ui/card";
import { Button } from "ui/button";
import { Badge } from "ui/badge";
import { Checkbox } from "ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/dialog";
import { Select, SelectContent, SelectItem, SelectValue } from "ui/select";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { InputFieldset } from "ui/inputFieldset";
import { Plus, Edit2, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  getSalaryHeads,
  SalaryHeadApi,
  getCurrencies,
  CurrencyApi,
  createEmployeeSalary,
  updateEmployeeSalary,
  deleteEmployeeSalary,
} from "services/employeeService";

export interface Facility {
  id: string;
  salaryHeadId?: number;
  currencyHeadId?: number;
  enabled: boolean;
  name: string;
  type: "Earning" | "Deduction";
  currency: string;
  amount: number;
  remuneration: string;
  wefDate: string;
  wetDate?: string;
  isSpecial?: boolean;
  leaveCalc?: boolean;
  remarks?: string;
  stopped?: boolean;
}

interface Props {
  employeeId: number;
  isViewMode?: boolean;
  facilities: Facility[];
  setFacilities: React.Dispatch<React.SetStateAction<Facility[]>>;
}

export default function FacilitiesTab({
  employeeId,
  facilities,
  setFacilities,
  isViewMode = false,
}: Props) {
  const [showFacilityDialog, setShowFacilityDialog] = useState(false);
  const [isFacilityEditMode, setIsFacilityEditMode] = useState(false);
  const [salaryHeads, setSalaryHeads] = useState<SalaryHeadApi[]>([]);
  const [currencies, setCurrencies] = useState<CurrencyApi[]>([]);
  const [editingFacilityId, setEditingFacilityId] = useState<string | null>(
    null,
  );
  const [facilityErrors, setFacilityErrors] = useState<Record<string, string>>(
    {},
  );
  const [isDeleteFacilityDialogOpen, setIsDeleteFacilityDialogOpen] =
    useState(false);
  const [deletingFacility, setDeletingFacility] = useState<Facility | null>(
    null,
  );

  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const [salaryRes, currencyRes] = await Promise.all([
          getSalaryHeads(),
          getCurrencies(),
        ]);

        setSalaryHeads(salaryRes);

        setCurrencies(currencyRes.filter((item) => item.isActive));
      } catch (error) {
        console.error(error);

        toast.error("Failed to load dropdown data");
      }
    };

    loadDropdowns();
  }, []);

  const [newFacility, setNewFacility] = useState({
    enabled: true,

    name: "",

    type: "Earning" as "Earning" | "Deduction",

    currency: "USD",

    amount: 0,

    remuneration: "Monthly",

    wefDate: "",
  });

  const addFacility = async () => {
    const errors: Record<string, string> = {};

    if (!newFacility.name) {
      errors.name = "Salary Head is required";
    }

    if (!newFacility.currency) {
      errors.currency = "Currency is required";
    }

    if (!newFacility.amount || newFacility.amount <= 0) {
      errors.amount = "Amount is required";
    }

    if (!newFacility.remuneration) {
      errors.remuneration = "Remuneration is required";
    }

    if (!newFacility.wefDate) {
      errors.wefDate = "WEF Date is required";
    }

    if (Object.keys(errors).length > 0) {
      setFacilityErrors(errors);

      toast.error("Please fill all required fields");

      return;
    }

    try {
      const selectedHead = salaryHeads.find(
        (h) => h.salaryHeadName === newFacility.name,
      );

      const selectedCurrency = currencies.find(
        (c) => c.currencyCode === newFacility.currency,
      );

      if (!selectedHead || !selectedCurrency) {
        toast.error("Invalid salary head or currency");

        return;
      }

      const payload = {
        employeeId,

        salaryHeadId: selectedHead.id,

        currencyHeadId: selectedCurrency.currencyId,

        amount: newFacility.amount,

        remuneration: newFacility.remuneration,

        withEffectFrom: newFacility.wefDate,

        isActive: newFacility.enabled,
      };

      const res = await createEmployeeSalary(payload);

      setFacilities((prev) => [
        ...prev,
        {
          id: res.id.toString(),
          salaryHeadId: res.salaryHeadId,
          currencyHeadId: res.currencyHeadId,
          enabled: res.isActive,
          name: res.salaryHeadName,
          type: selectedHead.type,
          currency: selectedCurrency.currencyCode,
          amount: res.amount,
          remuneration: res.remuneration,
          wefDate: res.withEffectFrom ? res.withEffectFrom.split("T")[0] : "",
          wetDate: "",
          isSpecial: false,
          leaveCalc: false,
          remarks: "",
          stopped: false,
        },
      ]);

      resetFacilityForm();

      toast.success("Salary component added successfully");
    } catch (error) {
      console.error(error);

      toast.error("Failed to save salary component");
    }
  };

  const editFacility = (facility: Facility) => {
    setNewFacility({
      enabled: facility.enabled,

      name: facility.name,

      type: facility.type,

      currency: facility.currency,

      amount: facility.amount,

      remuneration: facility.remuneration,

      wefDate: facility.wefDate,
    });

    setEditingFacilityId(facility.id);

    setIsFacilityEditMode(true);

    setShowFacilityDialog(true);
  };

  const updateFacility = async () => {
    try {
      const selectedHead = salaryHeads.find(
        (h) => h.salaryHeadName === newFacility.name,
      );

      const selectedCurrency = currencies.find(
        (c) => c.currencyCode === newFacility.currency,
      );

      const existingFacility = facilities.find(
        (f) => f.id === editingFacilityId,
      );

      if (!selectedHead || !selectedCurrency || !existingFacility) {
        toast.error("Invalid salary component");

        return;
      }

      const payload = {
        id: Number(editingFacilityId),

        employeeId,

        salaryHeadId: selectedHead.id,

        currencyHeadId: selectedCurrency.currencyId,

        amount: newFacility.amount,

        remuneration: newFacility.remuneration,

        withEffectFrom: newFacility.wefDate,

        isActive: newFacility.enabled,
      };

      const res = await updateEmployeeSalary(payload);

      const updatedFacilities = facilities.map((facility) =>
        facility.id === editingFacilityId
          ? {
              ...facility,

              enabled: res.isActive,

              name: res.salaryHeadName,

              currency: res.currencyHeadName,

              amount: res.amount,

              remuneration: res.remuneration,

              wefDate: res.withEffectFrom
                ? res.withEffectFrom.split("T")[0]
                : "",
            }
          : facility,
      );

      setFacilities(updatedFacilities);

      resetFacilityForm();

      toast.success("Salary component updated successfully");
    } catch (error) {
      console.error(error);

      toast.error("Failed to update salary component");
    }
  };

  const confirmDeleteFacility = async () => {
    try {
      if (!deletingFacility) return;

      await deleteEmployeeSalary(Number(deletingFacility.id));

      setFacilities((prev) =>
        prev.filter((item) => item.id !== deletingFacility.id),
      );

      setIsDeleteFacilityDialogOpen(false);

      setDeletingFacility(null);

      toast.success("Salary component deleted successfully");
    } catch (error) {
      console.error(error);

      toast.error("Failed to delete salary component");
    }
  };

  const resetFacilityForm = () => {
    setShowFacilityDialog(false);

    setIsFacilityEditMode(false);

    setEditingFacilityId(null);

    setFacilityErrors({});

    setNewFacility({
      enabled: true,

      name: "",

      type: "Earning",

      currency: "USD",

      amount: 0,

      remuneration: "Monthly",

      wefDate: "",
    });
  };

  return (
    <>
      <Card className="bg-white" style={{ gap: "10px" }}>
        <CardHeader>
          <CardTitle>Salary & Facilities</CardTitle>
        </CardHeader>

        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                  <TableHead style={{ color: "#5B9BD5" }}>Enabled</TableHead>
                  <TableHead style={{ color: "#5B9BD5" }}>
                    Name <span className="text-red-500">*</span>
                  </TableHead>
                  <TableHead style={{ color: "#5B9BD5" }}>
                    Type <span className="text-red-500">*</span>
                  </TableHead>
                  <TableHead style={{ color: "#5B9BD5" }}>
                    Currency <span className="text-red-500">*</span>
                  </TableHead>
                  <TableHead style={{ color: "#5B9BD5" }}>
                    Amount <span className="text-red-500">*</span>
                  </TableHead>
                  <TableHead style={{ color: "#5B9BD5" }}>
                    Remuneration <span className="text-red-500">*</span>
                  </TableHead>
                  <TableHead style={{ color: "#5B9BD5" }}>
                    WEF Date <span className="text-red-500">*</span>
                  </TableHead>
                  {!isViewMode && (
                    <TableHead
                      style={{
                        color: "#5B9BD5",
                      }}
                    >
                      Action
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {facilities.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center py-6 text-gray-500"
                    >
                      No salary components yet
                    </TableCell>
                  </TableRow>
                ) : (
                  facilities.map((facility) => (
                    <TableRow key={facility.id}>
                      <TableCell>
                        <Checkbox checked={facility.enabled} />
                      </TableCell>

                      <TableCell className="font-medium">
                        {facility.name}
                      </TableCell>

                      <TableCell>
                        <Badge
                          className={
                            facility.type === "Earning"
                              ? "bg-green-100 text-green-700 border border-green-300"
                              : "bg-red-100 text-red-700 border border-red-300"
                          }
                        >
                          {facility.type}
                        </Badge>
                      </TableCell>
                      <TableCell>{facility.currency}</TableCell>
                      <TableCell>{facility.amount}</TableCell>
                      <TableCell>{facility.remuneration}</TableCell>
                      <TableCell>{facility.wefDate}</TableCell>
                      {!isViewMode && (
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => editFacility(facility)}
                          >
                            <Edit2 className="w-4 h-4 text-[#5B9BD5]" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setDeletingFacility(facility);
                              setIsDeleteFacilityDialogOpen(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {!isViewMode && (
            <Button
              className="mt-4"
              variant="outline"
              onClick={() => {
                resetFacilityForm();

                setShowFacilityDialog(true);
              }}
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Salary Component
            </Button>
          )}
        </CardContent>
      </Card>

      {/* DIALOG */}
      {showFacilityDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <h3 className="text-lg font-bold" style={{ color: "#5B9BD5" }}>
                {isFacilityEditMode
                  ? "Edit Salary Component"
                  : "Add Salary Component"}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowFacilityDialog(false);

                  setIsFacilityEditMode(false);

                  setEditingFacilityId(null);

                  setNewFacility({
                    enabled: true,
                    name: undefined as unknown as string,
                    type: "Earning",
                    currency: "USD",
                    amount: 0,
                    remuneration: "Monthly",
                    wefDate: "",
                  });
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-4 space-y-4">
              <div className="space-y-2">
                <SelectFieldset label="Salary Head" required>
                  <Select
                    value={newFacility.name || undefined}
                    onValueChange={(value) => {
                      const selectedHead = salaryHeads.find(
                        (h) => h.salaryHeadName === value,
                      );
                      setNewFacility({
                        ...newFacility,
                        name: value,
                        type: selectedHead?.type || "Earning",
                      });
                    }}
                    disabled={isFacilityEditMode}
                  >
                    <SelectTriggerUI id="facilityName">
                      <SelectValue placeholder="Select salary head" />
                    </SelectTriggerUI>
                    <SelectContent>
                      {salaryHeads.map((head) => (
                        <SelectItem
                          key={head.publicId}
                          value={head.salaryHeadName}
                        >
                          {head.salaryHeadName} ({head.type})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </SelectFieldset>
                {facilityErrors.name && (
                  <p className="text-red-500 text-xs mt-1">
                    {facilityErrors.name}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <SelectFieldset label="Currency" required>
                  <Select
                    value={newFacility.currency}
                    onValueChange={(value) =>
                      setNewFacility({ ...newFacility, currency: value })
                    }
                    disabled={isFacilityEditMode}
                  >
                    <SelectTriggerUI id="facilityCurrency">
                      <SelectValue />
                    </SelectTriggerUI>
                    <SelectContent>
                      {currencies.map((currency) => (
                        <SelectItem
                          key={currency.currencyId}
                          value={currency.currencyCode}
                        >
                          {currency.currencyCode}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </SelectFieldset>
                {facilityErrors.currency && (
                  <p className="text-red-500 text-xs mt-1">
                    {facilityErrors.currency}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Amount"
                  required
                  type="number"
                  id="facilityAmount"
                  value={newFacility.amount}
                  onChange={(e) => {
                    setNewFacility({
                      ...newFacility,
                      amount: parseFloat(e.target.value) || 0,
                    });
                    setFacilityErrors({ ...facilityErrors, amount: "" });
                  }}
                  disabled={isFacilityEditMode}
                  className={`
                    ${facilityErrors.amount ? "border-red-500 border-1" : ""}
                    ${isFacilityEditMode ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""}
                  `}
                />
                {facilityErrors.amount && (
                  <p className="text-red-500 text-xs mt-1">
                    {facilityErrors.amount}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <SelectFieldset label="Remuneration" required>
                  <Select
                    value={newFacility.remuneration}
                    onValueChange={(value) =>
                      setNewFacility({ ...newFacility, remuneration: value })
                    }
                    disabled={isFacilityEditMode}
                  >
                    <SelectTriggerUI id="facilityRemuneration">
                      <SelectValue />
                    </SelectTriggerUI>
                    <SelectContent>
                      <SelectItem value="Monthly">Monthly</SelectItem>
                      <SelectItem value="Quarterly">Quarterly</SelectItem>
                      <SelectItem value="Annually">Annually</SelectItem>
                      <SelectItem value="One-time">One-time</SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>
                {facilityErrors.remuneration && (
                  <p className="text-red-500 text-xs mt-1">
                    {facilityErrors.remuneration}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="With Effect From (WEF) Date"
                  required
                  type="date"
                  id="facilityWefDate"
                  value={newFacility.wefDate}
                  onChange={(e) => {
                    setNewFacility({ ...newFacility, wefDate: e.target.value });
                    setFacilityErrors({ ...facilityErrors, wefDate: "" });
                  }}
                  disabled={isFacilityEditMode}
                  className={`
                    ${facilityErrors.wefDate ? "border-red-500 border-1" : ""}
                    ${isFacilityEditMode ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""}
                  `}
                />
                {facilityErrors.wefDate && (
                  <p className="text-red-500 text-xs mt-1">
                    {facilityErrors.wefDate}
                  </p>
                )}
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={newFacility.enabled}
                  onCheckedChange={(checked) =>
                    setNewFacility({ ...newFacility, enabled: !!checked })
                  }
                />
                <span className="text-sm">Enabled</span>
              </label>
            </div>
            <div className="sticky bottom-0 bg-white border-t p-4 flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowFacilityDialog(false);

                  setIsFacilityEditMode(false);

                  setEditingFacilityId(null);

                  setNewFacility({
                    enabled: true,
                    name: undefined as unknown as string,
                    type: "Earning",
                    currency: "USD",
                    amount: 0,
                    remuneration: "Monthly",
                    wefDate: "",
                  });
                }}
              >
                Cancel
              </Button>
              <Button
                style={{ background: "#5B9BD5" }}
                className="text-white"
                size="sm"
                onClick={isFacilityEditMode ? updateFacility : addFacility}
              >
                {isFacilityEditMode ? "Update Component" : "Add Component"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog
        open={isDeleteFacilityDialogOpen}
        onOpenChange={setIsDeleteFacilityDialogOpen}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-red-600">
              Delete Salary Component
            </DialogTitle>

            <DialogDescription>
              Are you sure you want to delete{" "}
              <span className="font-semibold">"{deletingFacility?.name}"</span>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteFacilityDialogOpen(false);
                setDeletingFacility(null);
              }}
            >
              No, Cancel
            </Button>

            <Button
              onClick={confirmDeleteFacility}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Yes, Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
